import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react"; 
const API_URL = process.env.REACT_APP_API_URL;
console.log("API URL from env:", process.env.REACT_APP_API_URL);
console.log("Final API Request URL:", `${API_URL}/api/auth/login`);
export default function SignIn() {
  const [loginType, setLoginType] = useState("email");
  const [username, setUsername] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); 
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const handleUsernameChange = (e) => {
    if (loginType === "email") {
      setUsername(e.target.value);
    } else {
      setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10)); 
    }
  }; 
  
  const handlePasswordChange = (e) => setPassword(e.target.value);

  // Helper function for redirect
  const redirectUser = (role, username) => {
    if (role === "ADMIN") {
      navigate("/admin");
    } else {
      navigate("/", { state: { username } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload =
      loginType === "email" ? { username, password } : { phoneNumber, password };
    try {
      // 1. First perform the login
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
  
      const isJson = response.headers.get("content-type")?.includes("application/json");
      let data = isJson ? await response.json() : await response.text();
  
      if (!response.ok) {
        const errorMessage = typeof data === "string" ? data : data.message || "Invalid credentials";
        setErrorMessage(errorMessage);
        return;
      }
  
      console.log("Login successful:", data);
  
      if (!data.token) {
        setErrorMessage("Login successful but missing token. Please try again.");
        return;
      }
  
      // 2. Store user data
      localStorage.setItem("token", data.token);
      sessionStorage.setItem("role", data.role);
      sessionStorage.setItem("userid", data.userid);
      sessionStorage.setItem("username", data.username);
  
      // 3. Process guest cart items
      const guestCart = JSON.parse(localStorage.getItem("guest_cart")) || [];
      if (guestCart.length === 0) {
        // No guest cart items - just redirect
        redirectUser(data.role, username);
        return;
      }

      console.log("Merging guest cart items:", guestCart);
      const token = data.token;
      const userId = data.userid;
      const errors = [];

      // Function to add items sequentially
      const addItemsSequentially = async () => {
        for (let i = 0; i < guestCart.length; i++) {
          const item = guestCart[i];
          try {
            console.log(`Adding item ${i + 1}/${guestCart.length}:`, item);
            
            const cartResponse = await fetch(`${API_URL}/api/cart/add`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                userId: userId,
                productId: item.productId,
                quantity: item.quantity || 1, // Default to 1 if quantity not specified
              }),
            });

            if (!cartResponse.ok) {
              const errorData = await cartResponse.json();
              throw new Error(errorData.message || `Failed to add product: ${item.title}`);
            }

            console.log(`Successfully added item ${i + 1}:`, item.productId);
          } catch (error) {
            console.error(`Failed to add item ${i + 1}:`, error);
            errors.push({
              productId: item.productId,
              title: item.title || "Unknown product",
              error: error.message,
            });
          }
        }

        // Clean up guest cart regardless of errors
        localStorage.removeItem("guest_cart");

        // Show errors if any
        if (errors.length > 0) {
          const errorText = errors.map(e => `${e.title} (${e.productId}): ${e.error}`).join("\n");
          setErrorMessage(`Some items couldn't be added:\n${errorText}`);
        }

        // Redirect user
        redirectUser(data.role, username);
      };

      // Start the sequential addition process
      await addItemsSequentially();

    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("Error during login: " + error.message);
    }
  };

  return (
    <div className="relative min-h-screen bg-[url('https://images.unsplash.com/photo-1466781783364-36c955e42a7f?q=80&w=2942&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')] bg-cover bg-center flex items-center justify-center px-4">
      <div className="flex w-full max-w-7xl gap-x-10">
        {/* Left Image Section */}
        <div className="hidden lg:block w-1/2"></div>

        {/* Sign-In Form Section */}
        <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 py-12 lg:px-16">
          <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8 text-center"></div>

          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            {errorMessage && (
              <div className="mb-4 text-center text-sm text-red-500">
                {errorMessage}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Input */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-900">
                  {loginType === "email" ? "Email" : "Phone Number"}
                </label>
                <input
                  id="username"
                  name="username"
                  value={loginType === "email" ? username : phoneNumber}
                  className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                  onChange={handleUsernameChange}
                  placeholder={loginType === "email" ? "Enter your email" : "Enter your phone number"}
                />
              </div>

              {/* Password Input with Eye */}
              <div className="relative">
                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    className="block w-full rounded-md border-0 py-2 pr-12 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                    onChange={handlePasswordChange}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>

                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-sm font-medium text-green-600 hover:text-green-500"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button type="submit" className="w-full rounded-md bg-green-600 py-2 text-white">
                Sign in
              </button>
            </form>

            <p className="mt-10 text-center text-sm text-gray-500">
              Not a member?{" "}
              <button
                onClick={() => navigate("/register")}
                className="font-semibold text-green-600 hover:text-green-500"
              >
                Sign Up
              </button>
            </p>

            <div className="mt-4 text-center">
              <button
                onClick={() => setLoginType(loginType === "email" ? "phone" : "email")}
                className="text-sm font-semibold text-green-600 hover:text-green-500"
              >
                Login with {loginType === "email" ? "Phone Number" : "Email"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
